import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthService, BreadcrumbService, StateService } from 'src/app/services';
import { Role } from 'src/app/shared/models/role.model';
import { Router } from '@angular/router';
import { 
  IdentityWebApiClient,
  Client,
  ListApiClientsResponse,
  ListApiClientsRequest,
  ClientScope,
  UpdateApiClientRequest,
  UpdateApiClientResponse
} from 'identity-webapi-client';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { SituApiActionConfirmationDialogComponent } from '../situapi-action-confirmation-dialog/situapi-action-confirmation-dialog.component';

@Component({
  selector: 'situapi-manage-clients',
  templateUrl: './situapi-manage-clients.component.html',
  styleUrls: ['./situapi-manage-clients.component.less']
})
export class SituApiManageClientsComponent implements OnInit, OnDestroy {
  admin = false;
  companyAdmin = false;
  clients: Client[] | undefined;
  companyId: string | undefined;

  private _subscriptions: Subscription[] = [];

  private _showLoading = false;
  public get showLoading(): boolean {
    return this._showLoading;
  }

  public set showLoading(val: boolean) {
    this._showLoading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    private identityWebApiClient: IdentityWebApiClient,
    private authService: AuthService,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private stateService: StateService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog) {
      this.admin = (this.authService.hasRole(Role.SituSystemsDeveloper) || this.authService.hasRole(Role.SituSystemsAdmin));
      this.companyAdmin = (this.authService.hasRole(Role.BuilderAdmin) || this.authService.hasRole(Role.Admin));
    }

  ngOnInit(): void {
    this._subscriptions.push(this.stateService.state$.subscribe(x => {
      this.companyId = x.company?.id ?? undefined;
      this.loadClients();
    }));
  }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      for (let sub of this._subscriptions) {
        sub.unsubscribe();
      }
      this._subscriptions = [];
    }
  }

  generateNewApiKey(client: Client) {
    const dialogRef = this.dialog.open(SituApiActionConfirmationDialogComponent, {
      width: '350px',
      data: { newKeyRequest: true }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (!client) return;
        this.showLoading = true;
    
        let request = new UpdateApiClientRequest();
        request.companyId = this.companyId;
        request.id = client.id;
        request.generateNewApiKey = true;
    
        return this.updateClient(request, client.clientId);
      } else {
        this.showMessage("New Client ApiKey Request not Sent!");
      }
    });
  } 

  toggleClientEnabled(client: Client) {
    const dialogRef = this.dialog.open(SituApiActionConfirmationDialogComponent, {
      width: '350px',
      data: { newKeyRequest: false }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (!client) return;
        this.showLoading = true;
    
        let request = new UpdateApiClientRequest();
        request.companyId = this.companyId;
        request.id = client.id;
        request.disableClient = client.enabled;
    
        return this.updateClient(request, client.clientId);
      } else {
        this.showMessage(`${client.enabled ? "Disable" : "Enable"} Client Request not Sent!`);
      }
    });
  } 

  deleteClient(client: Client) {
    const dialogRef = this.dialog.open(SituApiActionConfirmationDialogComponent, {
      width: '350px',
      data: { newKeyRequest: false }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (!client) return;
        this.showLoading = true;

        let request = new UpdateApiClientRequest();
        request.companyId = this.companyId;
        request.id = client.id;
        request.deleteClient = true;

        return this.updateClient(request, client.clientName);
      } else {
        this.showMessage("Delete Client Request not Sent!");
      }
    });
  } 

  onAddClient(){
    this.router.navigate(["/docs/situapi/add-client"]);
  }

  displayScopes(allowedScopes: ClientScope[]): string {
    return allowedScopes.map(x => x.scope).join(" ");
  }

  private loadClients() {
    this.showLoading = true;

    let request = new ListApiClientsRequest();
    request.companyId = this.companyId;

    return this.identityWebApiClient.listApiClients(request)
      .then((response: any) => {
        let listApiClientsResponse = response as ListApiClientsResponse;
        if (listApiClientsResponse.success) {
          this.clients = listApiClientsResponse.clients;
        } else {
          if (listApiClientsResponse?.errorMessages) {
            this.showMessage(listApiClientsResponse.errorMessages.join("\r\n"));
          }
        }
        this.showLoading = false;
      })
      .catch((err: any) => {
        this.showMessage("Failed to Fetch Api Clients!");
        this.showLoading = false;
      });
  }

  private updateClient(request: UpdateApiClientRequest, clientName: string | undefined) {
    return this.identityWebApiClient.updateApiClient(request)
      .then((response: any) => {
        let updateApiClientsResponse = response as UpdateApiClientResponse;
        if (updateApiClientsResponse.success) {
          if (updateApiClientsResponse.oneTimeApiKey) {
            this.dialog.open(SituApiActionConfirmationDialogComponent, {
              width: '450px',
              data: { 
                oneTimeApiKey: updateApiClientsResponse.oneTimeApiKey,
                clientName: clientName }
            });
          }
          this.loadClients();
        } else {
          if (updateApiClientsResponse?.errorMessages) {
            this.showMessage(updateApiClientsResponse.errorMessages.join("\r\n"));
          }
        }
        this.showLoading = false;
      })
      .catch((err: any) => {
        this.showMessage("Failed to Update Api Client!");
        this.showLoading = false;
      });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'Dismiss', {
      duration: 3000
    });
  }
}
